import { createColumnHelper } from '@tanstack/react-table';
import { Edit } from 'lucide-react';

import { truncate } from '@/portainer/filters/filters';

import { Badge } from '@@/Badge';
import { Switch } from '@@/form-components/SwitchField/Switch';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';
import { Link } from '@@/Link';
import { Button } from '@@/buttons';

import { AlertRuleRow } from '../../types';
import { AlertSeverityBadge } from '../../AlertSeverityBadge';
import { formatThreshold } from '../../utils';
import {
  getAlertRuleCategoryLabel,
  resolveAlertRuleCategory,
} from '../alertRuleCategory';

export const columnHelper = createColumnHelper<AlertRuleRow>();

export function createColumns(
  onToggle: (alertRule: AlertRuleRow, enabled: boolean) => void,
  togglingId?: number,
  isAlertManagerEnabled?: boolean
) {
  return [
    columnHelper.accessor('name', {
      header: 'Alert Rule',
      meta: { width: '42%' },
      cell: ({ getValue, row }) => {
        const {
          isInternal,
          supportedAgentVersion,
          supportedEnvironmentTypes,
          useTiers,
          category,
        } = row.original;

        const resolvedCategory = resolveAlertRuleCategory({
          category,
          labels: row.original.labels,
          supportedEnvironmentTypes,
        });

        return (
          <div className="flex flex-col gap-1">
            <span className="whitespace-nowrap font-medium">{getValue()}</span>
            <div className="flex flex-nowrap gap-1 overflow-hidden">
              <Badge type="info" className="text-xs">
                {getAlertRuleCategoryLabel(resolvedCategory)}
              </Badge>
              {isInternal && (
                <Badge type="info" className="text-xs">
                  System
                </Badge>
              )}
              {useTiers && (
                <Badge type="muted" className="text-xs">
                  Multi-tier
                </Badge>
              )}
              {supportedEnvironmentTypes && (
                <Badge type="success" className="text-xs">
                  {supportedEnvironmentTypes}
                </Badge>
              )}
              {supportedAgentVersion && (
                <Badge type="warn" className="text-xs">
                  {supportedAgentVersion}
                </Badge>
              )}
            </div>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      meta: { width: '8%' },
      cell: ({ row }) => {
        const alertRule = row.original;
        const isDisabledDueToNoAlertManager =
          !alertRule.enabled && !isAlertManagerEnabled;

        const switchComponent = (
          <Switch
            id={`toggle-alert-rule-${alertRule.id}`}
            name={`toggle-alert-rule-${alertRule.id}`}
            checked={alertRule.enabled}
            onChange={() => onToggle(alertRule, !alertRule.enabled)}
            disabled={
              togglingId === alertRule.id || isDisabledDueToNoAlertManager
            }
            data-cy={`toggle-alert-rule-${alertRule.id}`}
          />
        );

        const message =
          'Enable the internal alert manager in Alert Settings to activate alert rules';
        return (
          <div className="flex items-center gap-2">
            {isDisabledDueToNoAlertManager ? (
              <TooltipWithChildren message={message} position="top">
                <div className="inline-block cursor-not-allowed">
                  {switchComponent}
                </div>
              </TooltipWithChildren>
            ) : (
              switchComponent
            )}
            {isDisabledDueToNoAlertManager ? (
              <TooltipWithChildren message={message} position="top">
                <div className="inline-block cursor-not-allowed">
                  <Button
                    color="primary"
                    size="xsmall"
                    icon={Edit}
                    disabled
                    data-cy={`edit-alert-rule-${alertRule.id}-button`}
                  >
                    Edit
                  </Button>
                </div>
              </TooltipWithChildren>
            ) : (
              <>
                {!alertRule.isEditable ? (
                  <Button
                    color="primary"
                    size="xsmall"
                    icon={Edit}
                    disabled
                    data-cy={`edit-alert-rule-${alertRule.id}-button`}
                  >
                    Edit
                  </Button>
                ) : (
                  <Link
                    to="portainer.alerting.rule"
                    params={{
                      id: alertRule.id,
                    }}
                    data-cy={`edit-alert-rule-${alertRule.id}`}
                  >
                    <Button
                      color="primary"
                      size="xsmall"
                      icon={Edit}
                      data-cy={`edit-alert-rule-${alertRule.id}-button`}
                    >
                      Edit
                    </Button>
                  </Link>
                )}
              </>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor('description', {
      header: 'Description',
      meta: { width: '15%' },
      cell: ({ getValue }) => {
        const description = getValue();
        if (!description) return '-';
        if (description.length <= 60) return description;
        return (
          <TooltipWithChildren message={description} position="top">
            <span>{truncate(description, 60)}</span>
          </TooltipWithChildren>
        );
      },
    }),
    columnHelper.accessor('severity', {
      header: 'Severity',
      meta: { width: '6%' },
      cell: ({ getValue }) => <AlertSeverityBadge severity={getValue()} />,
    }),
    columnHelper.accessor('threshold', {
      header: 'Threshold',
      meta: { width: '7%' },
      cell: ({ getValue, row }) => {
        const { useTiers, tiers, metricType } = row.original;

        if (useTiers && tiers?.length) {
          const activeTiers = tiers.filter((t) => t.enabled);
          if (activeTiers.length === 0) return '-';
          return activeTiers
            .map(
              (t) => `${t.severity} ${formatThreshold(t.threshold, metricType)}`
            )
            .join(' / ');
        }

        const threshold = getValue();
        if (threshold === undefined || threshold === null) {
          return '-';
        }
        return formatThreshold(threshold, metricType);
      },
    }),
    columnHelper.accessor('conditionOperator', {
      header: 'Condition',
      meta: { width: '6%' },
      cell: ({ getValue, row }) => {
        const operator = getValue();
        const { threshold } = row.original;

        if (threshold === undefined || threshold === null) {
          return '-';
        }
        return operator || '>';
      },
    }),
    columnHelper.accessor('duration', {
      header: 'Duration (min)',
      meta: { width: '7%' },
      cell: ({ getValue }) => {
        const duration = getValue();
        return duration ? `${duration}m` : '-';
      },
    }),
    columnHelper.accessor('createdAt', {
      header: 'Created At',
      meta: { width: '9%' },
    }),
  ];
}
